/**
 * Collection and search results: filtering, sorting, pagination and quick add.
 *
 * One implementation serves both pages. <facet-container> does not know
 * whether it is wrapping a collection or a search result — it reads the base
 * URL off its own dataset and re-fetches its own section, so the two pages
 * share every line of this file.
 *
 * The component never decides what the results are. It builds a URL from the
 * form controls, hands that URL to Shopify, and swaps in whatever Liquid
 * rendered. Product counts, filter counts, which values are available and
 * which are disabled all arrive from the server; nothing here recalculates
 * them, so the page can never show a number that the next request disagrees
 * with.
 */
(function () {
  'use strict';

  const { utils, a11y, cart, strings } = window.Theme;

  /** Media query for "the filters are a drawer, not a sidebar". */
  const mobile = window.matchMedia('(max-width: 989px)');

  if (!customElements.get('facet-container')) {
    class FacetContainer extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.baseUrl = this.dataset.baseUrl || window.location.pathname;
        this.controller = null;

        // Remember the URL this markup was rendered for, so popstate can tell
        // a real history move from the pushState this component just did.
        this.renderedUrl = window.location.href;

        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPopState = this.onPopState.bind(this);

        // Price inputs fire `change` on every keystroke commit; a short debounce
        // keeps a two-field range from firing two requests.
        this.debouncedApply = utils.debounce(() => this.applyFromForms(), 400);

        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('popstate', this.onPopState);

        this.dropNoJsMirrors();
        this.syncActiveCount();
      }

      /**
       * Each form carries a hidden copy of the other form's state so that a
       * visitor without JavaScript does not lose their sort when they apply a
       * filter. With JavaScript, buildUrl() reads both forms directly, and
       * those copies become a second, stale value for the same parameter —
       * so they are disabled, which removes them from FormData.
       */
      dropNoJsMirrors() {
        this.querySelectorAll('[data-nojs-mirror]').forEach((input) => {
          input.disabled = true;
        });
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('popstate', this.onPopState);
        this.controller?.abort();
        if (this.dataset.drawerOpen === 'true') this.closeDrawer(false);
      }

      /* ---------------------------------------------------------------- */
      /* Events                                                            */
      /* ---------------------------------------------------------------- */

      onChange(event) {
        const field = event.target.closest('[data-facet-form] input, [data-facet-form] select');
        if (!field) return;

        if (field.type === 'number') this.debouncedApply();
        else this.applyFromForms();
      }

      onSubmit(event) {
        const form = event.target.closest('[data-facet-form]');
        if (!form) return;
        // Without JS this submit navigates; with JS it becomes a fetch.
        event.preventDefault();
        this.applyFromForms();
      }

      onClick(event) {
        if (event.target.closest('[data-facet-open]')) {
          event.preventDefault();
          this.openDrawer();
          return;
        }

        if (event.target.closest('[data-facet-close]')) {
          event.preventDefault();
          this.closeDrawer();
          return;
        }

        if (event.target.closest('[data-facet-overlay]')) {
          this.closeDrawer();
          return;
        }

        // Active-filter chips, "clear all", and pagination links are all real
        // hrefs. Intercepting them keeps the AJAX path and the no-JS path
        // pointing at exactly the same URLs.
        const link = event.target.closest('[data-facet-remove], .pagination a');
        if (link && link.href) {
          event.preventDefault();
          this.closeDrawer(false);
          this.render(link.href, { scroll: link.matches('.pagination a') });
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.dataset.drawerOpen === 'true') {
          event.preventDefault();
          this.closeDrawer();
        }
      }

      onPopState() {
        // Back and forward must show the results for the URL the browser moved
        // to, not re-push a new entry.
        if (window.location.href === this.renderedUrl) return;
        this.render(window.location.href, { history: false });
      }

      /* ---------------------------------------------------------------- */
      /* URL building                                                      */
      /* ---------------------------------------------------------------- */

      /**
       * Merges every facet form inside this container into one query string.
       *
       * There are two forms — the sort control in the toolbar and the filter
       * groups in the panel — because a single form cannot be in two places in
       * the layout. Reading both here is what keeps a sort change from
       * dropping the filters and vice versa.
       */
      buildUrl() {
        const params = new URLSearchParams();

        // Both forms carry the hidden search term, so the same key/value pair
        // arrives twice. Two different values of one key are meaningful —
        // that is how a multi-select filter is expressed — but the identical
        // pair never is, so dedupe on the pair rather than on the key.
        const seen = new Set();

        this.querySelectorAll('[data-facet-form]').forEach((form) => {
          new FormData(form).forEach((value, key) => {
            // An empty price field means "no bound", not "bound of zero".
            if (String(value).trim() === '') return;

            const pair = `${key}=${value}`;
            if (seen.has(pair)) return;
            seen.add(pair);

            params.append(key, value);
          });
        });

        const query = params.toString();
        return query ? `${this.baseUrl}?${query}` : this.baseUrl;
      }

      applyFromForms() {
        this.render(this.buildUrl());
      }

      /* ---------------------------------------------------------------- */
      /* Rendering                                                         */
      /* ---------------------------------------------------------------- */

      /**
       * @param {string}  url
       * @param {object}  options
       * @param {boolean} options.history  push a history entry (default true)
       * @param {boolean} options.scroll   scroll the results into view
       */
      async render(url, options = {}) {
        const { history = true, scroll = false } = options;

        // A visitor ticking three boxes quickly fires three requests; only the
        // last one's markup should ever reach the page.
        this.controller?.abort();
        this.controller = new AbortController();

        this.setLoading(true);

        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('section_id', this.sectionId);

        try {
          const response = await fetch(requestUrl.toString(), { signal: this.controller.signal });
          if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);

          const html = await response.text();
          const fresh = utils.parseSection(html, 'facet-container');
          if (!fresh) throw new Error('Filter response contained no results');

          this.swap(fresh, url, history, scroll);
        } catch (error) {
          if (error.name === 'AbortError') return;
          // A failed filter must not leave the visitor on a stale list that no
          // longer matches the controls, so fall back to a real navigation.
          window.location.href = url;
        } finally {
          if (this.controller && !this.controller.signal.aborted) this.setLoading(false);
        }
      }

      swap(fresh, url, history, scroll) {
        // Keep the drawer open across a filter change on mobile: the visitor is
        // still choosing, and closing it after every tick would be unusable.
        const wasOpen = this.dataset.drawerOpen === 'true';
        const focusedId = document.activeElement?.id;
        const selectionStart = document.activeElement?.selectionStart;

        this.innerHTML = fresh.innerHTML;
        this.className = fresh.className;

        if (history) {
          window.history.pushState({ facets: true }, '', url);
        }
        this.renderedUrl = window.location.href;

        if (wasOpen) this.dataset.drawerOpen = 'true';
        this.dropNoJsMirrors();
        this.syncActiveCount();

        // Put the caret back where it was, or a price field loses focus after
        // every keystroke that triggers a refetch.
        if (focusedId) {
          const restored = this.querySelector(`#${CSS.escape(focusedId)}`);
          if (restored) {
            restored.focus({ preventScroll: true });
            if (selectionStart != null && restored.setSelectionRange && restored.type !== 'number') {
              try {
                restored.setSelectionRange(selectionStart, selectionStart);
              } catch (e) {
                /* not a text input */
              }
            }
          }
        }

        if (scroll) {
          const top = this.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top, behavior: utils.scrollBehavior() });
        }

        document.dispatchEvent(new CustomEvent('facets:updated', { detail: { url }, bubbles: true }));
      }

      setLoading(isLoading) {
        this.toggleAttribute('data-loading', isLoading);
        const indicator = this.querySelector('[data-results-loading]');
        if (indicator) indicator.hidden = !isLoading;
        const main = this.querySelector('[data-results-main]');
        if (main) main.setAttribute('aria-busy', String(isLoading));
      }

      /** Mirrors the number of active filters onto the mobile toggle. */
      syncActiveCount() {
        const badge = this.querySelector('[data-facet-count]');
        if (!badge) return;

        // Counted from the rendered chips, which Liquid produced from
        // `filter.active_values` — not from the checkboxes, which would count a
        // box the visitor has ticked but whose request has not landed yet.
        const chips = this.querySelectorAll('[data-facet-active] .facet-chip:not(.facet-chip--clear)');
        badge.textContent = chips.length ? String(chips.length) : '';
        badge.hidden = chips.length === 0;
      }

      /* ---------------------------------------------------------------- */
      /* Mobile drawer                                                     */
      /* ---------------------------------------------------------------- */

      openDrawer() {
        if (!mobile.matches) return;
        const panel = this.querySelector('[data-facet-panel]');
        if (!panel) return;

        this.dataset.drawerOpen = 'true';
        this.querySelectorAll('[data-facet-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
        a11y.lockScroll();
        a11y.trapFocus(panel, panel.querySelector('[data-facet-close]') || panel);
      }

      closeDrawer(returnFocus = true) {
        if (this.dataset.drawerOpen !== 'true') return;
        delete this.dataset.drawerOpen;
        this.querySelectorAll('[data-facet-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        a11y.removeTrapFocus(returnFocus);
        a11y.unlockScroll();
      }
    }

    customElements.define('facet-container', FacetContainer);
  }

  /* ------------------------------------------------------------------ */
  /* Quick add                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Quick add on a product card.
   *
   * Cards are re-rendered on every filter change, so this is delegated from
   * the document rather than bound per button. It goes through Theme.cart —
   * the same path the product page and the sticky bar use — so there is still
   * exactly one add-to-cart implementation in the theme.
   *
   * Only single-variant products get a button. A product with options renders
   * a link to its page instead, because adding "the first variant" for someone
   * who has not chosen a size is a returns problem, not a conversion win.
   */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;

    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const original = button.textContent.trim();
    button.disabled = true;
    button.classList.add('button--loading');

    try {
      await cart.add({ id: Number(variantId), quantity: 1 });
      button.textContent = strings.added || original;
      setTimeout(() => {
        button.textContent = original;
        button.classList.remove('button--loading');
        button.disabled = false;
      }, 1600);
    } catch (error) {
      button.textContent = original;
      button.classList.remove('button--loading');
      button.disabled = false;

      const live = document.getElementById('a11y-live-region');
      if (live) live.textContent = error.data?.description || strings.cartError || '';
    }
  });

  // Re-sync after the editor re-renders a section.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelector('facet-container')?.syncActiveCount();
  });
})();
